import type {
  A2AssetDraft,
  A2Record,
  A3CommunityDraft,
  A3OfficialDraft,
  Engagement,
  GeometryVersion,
  LocationMethod,
  LocationProvenance,
  Stage2State,
} from "./types";
import {
  defaultGeometryType,
  infrastructureRepository,
  infrastructureType,
  infrastructureTypes,
  type InfrastructureTypeDefinition,
} from "./symbols/repository";
import {
  legacyLocationMethod,
  migrateLegacyCondition,
  STAGE2_SCHEMA_VERSION,
} from "./config";
import { buildEnrichmentCards } from "./enrichmentConfig";

const today = () => new Date().toISOString().slice(0, 10);

export const assetTypes = infrastructureTypes.map((type) => ({
  code: type.type_code,
  label: type.label,
  geometry: defaultGeometryType(type.type_code),
}));

export const communityAreas = [
  { code: "Germiston area", label: "Germiston area" },
  { code: "Soweto area", label: "Soweto area" },
];

export const engagementOutcomeCodes = [
  "NOT_ATTEMPTED",
  "NO_ANSWER",
  "CALL_BACK",
  "LINK_SENT",
  "ASSISTED_STARTED",
  "COMPLETED",
  "DECLINED",
  "INELIGIBLE / WRONG_CONTACT",
  "INFORMATION_UNAVAILABLE",
  "DOCUMENT_REFUSED / ACCESS_RESTRICTED",
];

export function createA2Draft(): A2AssetDraft {
  const now = new Date().toISOString();
  const commonCore = {
    assetName: "",
    operationalStatus: "",
    owner: "",
    operator: "",
    informationDate: today(),
    sourceType: "",
    sourceReference: "",
    confidence: "",
    sensitivity: "project-restricted" as const,
    notes: "",
  };
  return {
    schemaVersion: STAGE2_SCHEMA_VERSION,
    assetId: crypto.randomUUID(),
    municipalityCode: "",
    settlementArea: "",
    assetType: "",
    repositoryVersion: infrastructureRepository.repository_version,
    assetName: "",
    aliasNames: "",
    localReference: "",
    officialAssetId: "",
    operationalStatus: "",
    owner: "",
    operator: "",
    geometry: null,
    locationMethod: "",
    locationConfidence: "",
    gpsAccuracyM: "",
    pendingLocationProvenance: { provider: "local" },
    geometryVersions: [],
    currentGeometrySeq: 0,
    commonCore,
    quickFacts: {},
    typeHistory: [],
    enrichment: buildEnrichmentCards([]),
    otherDetails: {
      shortName: "",
      geometryType: "Point",
      nearestCategory: "",
      reason: "",
    },
    duplicateAcknowledged: false,
    importBatchId: "",
    importRowNumber: null,
    capacityValue: "",
    capacityUnit: "",
    capacityBasis: "",
    conditionScore: "not_inspected_unknown",
    conditionBasis: "",
    visibleIssues: [],
    commissioningYear: "",
    lastKnownInspection: "",
    serviceAreas: "",
    populationServed: "",
    populationYear: "",
    populationBasis: "",
    sourceType: "",
    sourceReference: "",
    informationDate: today(),
    confidence: "",
    sensitivity: "project-restricted",
    notes: "",
    evidence: [],
    syncState: "local_draft",
    resume: { step: "locate", sheetState: "half" },
    createdAt: now,
    updatedAt: now,
  };
}

export function createA3OfficialDraft(): A3OfficialDraft {
  return {
    responseId: crypto.randomUUID(),
    officialRole: "",
    organisation: "",
    serviceArea: "",
    coverageEstimatePct: "",
    coverageYear: "",
    coverageBasis: "",
    mainSupplyArrangements: [],
    mainSupplyOther: "",
    typicalSupplyDays: "",
    interruptions: "",
    interruptionReferencePeriod: "Current typical conditions",
    interruptionDuration: "",
    advanceNoticePractice: "",
    tankerDependence: "",
    tankerAreas: "",
    complaintVolume: "",
    complaintPeriod: "",
    complaintBasis: "",
    responseTime: "",
    responseTimeMeasures: "",
    reliabilityRating: "",
    reliabilityReason: "",
    biggestConstraint: "",
    constraintDetail: "",
    recentChange: "",
    recentChangeReason: "",
    sourceReferences: "",
    permissionToFollowUp: "",
  };
}

export function createA3CommunityDraft(batchCode = ""): A3CommunityDraft {
  return {
    responseId: crypto.randomUUID(),
    areaCode: "",
    mainWaterSources: [],
    mainWaterSource: "",
    daysAvailable: "",
    daysVaries: false,
    daysNote: "",
    interruptionFrequency: "",
    noticeFrequency: "",
    pressureRating: "",
    safetyConfidence: "",
    collectionTime: "",
    tankerFrequency: "",
    tankerReliability: "",
    complaintSatisfaction: "",
    overallSatisfaction: "",
    situationChange: "",
    situationChangeReason: "",
    priorityChange: "",
    representativeProvided: "",
    representativeType: "",
    batchCode,
    respondentName: "",
    respondentPhone: "",
  };
}

export function createEngagement(): Engagement {
  return {
    engagementId: crypto.randomUUID(),
    engagementType: "professional-contact",
    outcomeCode: "NOT_ATTEMPTED",
    representativeType: "",
    organisation: "",
    areaCode: "",
    consultationDate: today(),
    topics: "",
    responsesFacilitated: "0",
    permissionToFollowUp: "no",
    restrictedContact: "",
    nextActionDate: "",
    batchCode: `WSI-${Math.random().toString(36).slice(2, 7).toUpperCase()}`,
  };
}

export function createStage2State(): Stage2State {
  return {
    schemaVersion: STAGE2_SCHEMA_VERSION,
    a2Draft: createA2Draft(),
    assets: [],
    importBatches: [],
    a3OfficialDraft: createA3OfficialDraft(),
    a3OfficialRecords: [],
    a3CommunityDraft: createA3CommunityDraft(),
    a3CommunityRecords: [],
    engagements: [],
    favouriteTypeCodes: [],
    recentTypeCodes: [],
    updatedAt: new Date().toISOString(),
  };
}

export function commonCoreSnapshot(draft: A2AssetDraft) {
  return {
    assetName: draft.assetName,
    operationalStatus: draft.operationalStatus,
    owner: draft.owner,
    operator: draft.operator,
    informationDate: draft.informationDate,
    sourceType: draft.sourceType,
    sourceReference: draft.sourceReference,
    confidence: draft.confidence,
    sensitivity: draft.sensitivity,
    notes: draft.notes,
  };
}

export function withDraftMetadata(draft: A2AssetDraft): A2AssetDraft {
  return {
    ...draft,
    schemaVersion: STAGE2_SCHEMA_VERSION,
    commonCore: commonCoreSnapshot(draft),
    updatedAt: new Date().toISOString(),
  };
}

function lineLength(coordinates: number[][]) {
  let length = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const [lng1, lat1] = coordinates[index - 1];
    const [lng2, lat2] = coordinates[index];
    const meanLat = ((lat1 + lat2) / 2) * (Math.PI / 180);
    const dx = (lng2 - lng1) * 111_320 * Math.cos(meanLat);
    const dy = (lat2 - lat1) * 110_540;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return Math.round(length);
}

function polygonArea(coordinates: number[][]) {
  if (coordinates.length < 4) return 0;
  const meanLat =
    coordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) /
    coordinates.length;
  const points = coordinates.map(([lng, lat]) => [
    lng * 111_320 * Math.cos((meanLat * Math.PI) / 180),
    lat * 110_540,
  ]);
  let area = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    area +=
      points[index][0] * points[index + 1][1] -
      points[index + 1][0] * points[index][1];
  }
  return Math.round(Math.abs(area) / 2);
}

export function geometryProvenance(
  draft: A2AssetDraft,
): LocationProvenance {
  const geometry = draft.geometry;
  const provenance = { ...draft.pendingLocationProvenance };
  if (geometry?.type === "LineString") {
    provenance.derivedLengthM = lineLength(geometry.coordinates as number[][]);
  }
  if (geometry?.type === "Polygon") {
    provenance.derivedAreaM2 = polygonArea(
      (geometry.coordinates as number[][][])[0] ?? [],
    );
  }
  return { ...provenance, userConfirmed: true };
}

export function confirmDraftGeometry(draft: A2AssetDraft): A2AssetDraft {
  if (!draft.geometry) return draft;
  const method = (draft.locationMethod || legacyLocationMethod("")) as LocationMethod;
  const seq =
    Math.max(0, ...draft.geometryVersions.map((version) => version.seq)) + 1;
  const version: GeometryVersion = {
    seq,
    geometry: { ...draft.geometry, confirmed: true },
    method,
    provenance: geometryProvenance(draft),
    confirmedAt: new Date().toISOString(),
    superseded: false,
  };
  return withDraftMetadata({
    ...draft,
    geometry: version.geometry,
    geometryVersions: [
      ...draft.geometryVersions.map((item) => ({
        ...item,
        superseded: true,
      })),
      version,
    ],
    currentGeometrySeq: seq,
    resume: { ...draft.resume, step: draft.assetType ? "quick_facts" : "identify" },
  });
}

export function finalGeometryAllowed(draft: A2AssetDraft) {
  const definition = infrastructureType(draft.assetType);
  return Boolean(
    definition &&
      draft.geometry &&
      definition.allowed_geometry.includes(draft.geometry.type),
  );
}

export function applyTypeSelection(
  draft: A2AssetDraft,
  next: InfrastructureTypeDefinition,
): A2AssetDraft {
  const typeChanged = Boolean(
    draft.assetType && draft.assetType !== next.type_code,
  );
  const geometryAllowed = draft.geometry
    ? next.allowed_geometry.includes(draft.geometry.type)
    : true;
  const area = draft.settlementArea || draft.municipalityCode || "this area";
  return withDraftMetadata({
    ...draft,
    assetType: next.type_code,
    repositoryVersion: next.version,
    sensitivity: next.sensitivity_default,
    assetName:
      draft.assetName || `Unnamed ${next.label.toLowerCase()} near ${area}`,
    quickFacts:
      draft.assetType === next.type_code ? draft.quickFacts : {},
    typeHistory:
      typeChanged && Object.keys(draft.quickFacts).length
        ? [
            ...draft.typeHistory,
            {
              typeCode: draft.assetType,
              quickFacts: { ...draft.quickFacts },
              changedAt: new Date().toISOString(),
            },
          ]
        : draft.typeHistory,
    enrichment: buildEnrichmentCards(
      next.enrichment_cards,
      draft.assetType === next.type_code ? draft.enrichment : {},
    ),
    otherDetails:
      next.type_code === "OTHER"
        ? {
            ...draft.otherDetails,
            geometryType: draft.geometry?.type ?? "Point",
          }
        : draft.otherDetails,
    geometry: geometryAllowed
      ? draft.geometry
      : {
          type: next.allowed_geometry[0] ?? "Point",
          coordinates:
            (next.allowed_geometry[0] ?? "Point") === "Polygon" ? [[]] : [],
          confirmed: false,
        },
    resume: {
      ...draft.resume,
      step: geometryAllowed ? "quick_facts" : "locate",
    },
  });
}

function signedCoordinate(value: string, axis: "lat" | "lng") {
  const cleaned = value.trim().toUpperCase();
  const match = cleaned.match(
    /^([+-]?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?$/,
  );
  if (!match) return null;
  let number = Number(match[1]);
  const hemisphere = match[2];
  if (hemisphere) {
    const valid =
      axis === "lat"
        ? hemisphere === "N" || hemisphere === "S"
        : hemisphere === "E" || hemisphere === "W";
    if (!valid) return null;
    number = Math.abs(number) * (hemisphere === "S" || hemisphere === "W" ? -1 : 1);
  }
  return number;
}

export function parseCoordinateEntry(
  latitudeText: string,
  longitudeText: string,
):
  | {
      ok: true;
      coordinate: [number, number];
      originalLatitude: string;
      originalLongitude: string;
      warning?: string;
    }
  | { ok: false; message: string } {
  let latitudeValue = latitudeText.trim();
  let longitudeValue = longitudeText.trim();
  if (!longitudeValue && /[,;\s]\s*[+-]?\d/.test(latitudeValue)) {
    const parts = latitudeValue.split(/[,;\s]+/).filter(Boolean);
    if (parts.length === 2) {
      [latitudeValue, longitudeValue] = parts;
    }
  }
  let latitude = signedCoordinate(latitudeValue, "lat");
  let longitude = signedCoordinate(longitudeValue, "lng");
  let warning: string | undefined;
  if (
    latitude !== null &&
    longitude !== null &&
    Math.abs(latitude) > 90 &&
    Math.abs(longitude) <= 90
  ) {
    [latitude, longitude] = [longitude, latitude];
    warning = "The values appeared to be longitude, latitude and were normalised.";
  }
  if (
    latitude === null ||
    longitude === null ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180 ||
    (latitude === 0 && longitude === 0)
  ) {
    return {
      ok: false,
      message:
        "Enter WGS84 latitude then longitude. Latitude is −90 to 90; longitude is −180 to 180; 0, 0 is not accepted.",
    };
  }
  return {
    ok: true,
    coordinate: [
      Number(longitude.toFixed(6)),
      Number(latitude.toFixed(6)),
    ],
    originalLatitude: latitudeText,
    originalLongitude: longitudeText,
    warning,
  };
}

export function validateA2Asset(draft: A2AssetDraft) {
  const errors: Record<string, string> = {};
  const currentYear = new Date().getFullYear();
  if (!draft.assetType) errors.assetType = "Choose an asset type.";
  if (!draft.assetName.trim()) errors.assetName = "Enter an official or practical asset name.";
  if (!draft.operationalStatus) errors.operationalStatus = "Choose the current operational status.";
  if (!draft.owner.trim() && !draft.operator.trim()) errors.owner = "Record the owner or operator.";
  if (!draft.geometry?.confirmed) errors.geometry = "Capture and confirm the asset geometry.";
  if (!draft.locationMethod) errors.locationMethod = "Record how the location was obtained.";
  if (draft.capacityValue && !draft.capacityUnit) errors.capacityUnit = "Choose a unit for the capacity value.";
  if (draft.capacityUnit && !draft.capacityValue) errors.capacityValue = "Enter a value or clear the unit.";
  if (draft.capacityValue && Number(draft.capacityValue) <= 0) errors.capacityValue = "Capacity must be positive.";
  if (draft.capacityValue && !draft.capacityBasis) errors.capacityBasis = "Choose the capacity basis.";
  if (
    draft.commissioningYear &&
    (!Number.isInteger(Number(draft.commissioningYear)) ||
      Number(draft.commissioningYear) < 1800 ||
      Number(draft.commissioningYear) > currentYear)
  ) {
    errors.commissioningYear = `Enter a year from 1800 to ${currentYear}.`;
  }
  if (draft.populationServed && !draft.populationYear) {
    errors.populationYear = "Record the year for the population estimate.";
  }
  if (draft.populationServed && !draft.populationBasis) {
    errors.populationBasis = "Record the basis for the population estimate.";
  }
  if (draft.populationServed && Number(draft.populationServed) < 0) {
    errors.populationServed = "Population served cannot be negative.";
  }
  if (!draft.conditionScore) errors.conditionScore = "Choose a condition or Not inspected / unknown.";
  if (draft.conditionScore !== "not_inspected_unknown" && !draft.conditionBasis) {
    errors.conditionBasis = "Record what supports the condition score.";
  }
  if (!draft.sourceType) errors.sourceType = "Choose the primary source type.";
  if (!draft.sourceReference.trim()) errors.sourceReference = "Provide enough source detail for review.";
  if (!draft.informationDate) errors.informationDate = "Enter the information date.";
  if (!draft.confidence) errors.confidence = "Choose your confidence in the record.";
  return errors;
}

export function assetFlags(draft: A2AssetDraft) {
  const flags: string[] = [];
  if (draft.gpsAccuracyM && Number(draft.gpsAccuracyM) > 20) flags.push("LOCATION_UNCERTAIN");
  if (!draft.geometry) flags.push("LOCATION_UNCERTAIN");
  if (draft.conditionScore === "poor" || draft.conditionScore === "critical_failed") {
    flags.push("LOW_CONDITION");
  }
  if (draft.sensitivity === "sensitive") flags.push("EVIDENCE_RESTRICTED");
  if (draft.assetType === "OTHER") flags.push("OTHER_TYPE_REVIEW");
  return flags;
}

export function missionQuality(draft: A2AssetDraft) {
  const definition = infrastructureType(draft.assetType);
  const total = definition?.quick_facts.length ?? 0;
  const captured = definition
    ? definition.quick_facts.filter((field) => {
        const value = draft.quickFacts[field.field];
        return Array.isArray(value)
          ? value.length > 0
          : Boolean(value?.trim() && value !== "__UNKNOWN__");
      }).length
    : 0;
  const provenance = Boolean(
    draft.sourceType && draft.sourceReference.trim() && draft.informationDate,
  );
  const confidentLocation = Boolean(
    draft.geometry?.confirmed &&
      draft.locationMethod &&
      (draft.locationConfidence === "High" ||
        (draft.gpsAccuracyM && Number(draft.gpsAccuracyM) <= 20)),
  );
  const ratio = total ? captured / total : 0;
  const score = ratio * 55 + (provenance ? 25 : 0) + (confidentLocation ? 20 : 0);
  return {
    captured,
    total,
    badge: (score >= 80 ? "Gold" : score >= 50 ? "Silver" : "Bronze") as
      | "Bronze"
      | "Silver"
      | "Gold",
  };
}

export function validateMissionCore(draft: A2AssetDraft) {
  const errors: Record<string, string> = {};
  if (!draft.geometry?.confirmed) errors.geometry = "Confirm the infrastructure location.";
  if (!draft.locationMethod) errors.locationMethod = "Choose how the location was obtained.";
  if (!draft.locationConfidence) errors.locationConfidence = "Choose your confidence in the location.";
  if (!draft.assetType) errors.assetType = "Choose an infrastructure symbol.";
  if (draft.assetType && !finalGeometryAllowed(draft)) {
    errors.geometry = "Choose and confirm a geometry allowed for this infrastructure type.";
  }
  if (draft.assetType === "OTHER") {
    if (!draft.otherDetails.shortName.trim()) {
      errors.otherShortName = "Give this unclassified infrastructure a short name.";
    }
    if (!draft.otherDetails.nearestCategory) {
      errors.otherCategory = "Choose the nearest infrastructure category.";
    }
    if (!draft.otherDetails.reason.trim()) {
      errors.otherReason = "Explain briefly why none of the listed types fits.";
    }
  }
  if (!draft.assetName.trim()) errors.assetName = "Enter an asset name or use the suggested unnamed label.";
  if (!draft.operationalStatus) errors.operationalStatus = "Choose the operational status.";
  if (!draft.informationDate) errors.informationDate = "Enter the information date.";
  if (!draft.sourceType) errors.sourceType = "Choose the information source.";
  if (!draft.sourceReference.trim()) errors.sourceReference = "Enter a source reference.";
  if (!draft.confidence) errors.confidence = "Choose your confidence in the information.";
  return errors;
}

export function toA2Record(
  draft: A2AssetDraft,
  captureDisposition: "complete" | "enrich-later" = "complete",
): A2Record {
  const flags = assetFlags(draft);
  const quality = missionQuality(draft);
  return {
    ...withDraftMetadata(draft),
    syncState:
      captureDisposition === "complete" ? "ready_to_sync" : "local_draft",
    evidence: [...draft.evidence],
    status:
      captureDisposition === "enrich-later"
        ? "Draft"
        : flags.length
          ? "Auto-Flagged"
          : "Submitted",
    submittedAt: new Date().toISOString(),
    flags,
    qualityBadge: quality.badge,
    quickFactsCaptured: quality.captured,
    captureDisposition,
  };
}

export interface DraftReuseSelection {
  settlementArea?: boolean;
  source?: boolean;
  ownerOperator?: boolean;
  operationalStatus?: boolean;
  assetType?: boolean;
}

export function createNextA2Draft(
  previous: A2AssetDraft,
  selection: DraftReuseSelection = {},
): A2AssetDraft {
  const next = createA2Draft();
  return withDraftMetadata({
    ...next,
    municipalityCode: selection.settlementArea
      ? previous.municipalityCode
      : next.municipalityCode,
    settlementArea: selection.settlementArea ? previous.settlementArea : "",
    sourceType: selection.source ? previous.sourceType : "",
    sourceReference: selection.source ? previous.sourceReference : "",
    informationDate: selection.source ? previous.informationDate : today(),
    confidence: selection.source ? previous.confidence : "",
    owner: selection.ownerOperator ? previous.owner : "",
    operator: selection.ownerOperator ? previous.operator : "",
    operationalStatus: selection.operationalStatus
      ? previous.operationalStatus
      : "",
    assetType: selection.assetType ? previous.assetType : "",
    repositoryVersion: previous.repositoryVersion,
  });
}

export function normalizeStage2State(state: Stage2State): Stage2State {
  const fresh = createStage2State();
  const sourceState = (state ?? {}) as Stage2State;
  const sourceSchemaVersion = Number(sourceState.schemaVersion ?? 0);
  const normalizeEvidence = (
    item: Partial<A2AssetDraft["evidence"][number]>,
    sensitivity: A2AssetDraft["sensitivity"],
  ): A2AssetDraft["evidence"][number] => ({
    id: item.id ?? crypto.randomUUID(),
    name: item.name ?? item.originalName ?? "Evidence",
    type: item.type ?? item.originalType ?? "application/octet-stream",
    size: item.size ?? item.originalSize ?? 0,
    lastModified: item.lastModified ?? Date.now(),
    caption: item.caption ?? "",
    permissionBasis: item.permissionBasis ?? "",
    sensitivity: item.sensitivity ?? sensitivity,
    state: item.state ?? "local_only",
    sha256: item.sha256 ?? "",
    originalName: item.originalName ?? item.name ?? "Evidence",
    originalType: item.originalType ?? item.type ?? "application/octet-stream",
    originalSize: item.originalSize ?? item.size ?? 0,
    validationMessage: item.validationMessage,
    blob: item.blob,
  });
  const normalizeDraft = (input: A2AssetDraft): A2AssetDraft => {
    const draft = { ...fresh.a2Draft, ...(input ?? {}) };
    const legacyMethod = draft.locationMethod
      ? legacyLocationMethod(draft.locationMethod)
      : "";
    const geometryVersions: GeometryVersion[] =
      draft.geometryVersions?.length
        ? draft.geometryVersions
        : draft.geometry?.confirmed
          ? [
              {
                seq: 1,
                geometry: { ...draft.geometry, confirmed: true },
                method: (legacyMethod || "map_pin") as LocationMethod,
                provenance: {
                  provider: (
                    legacyMethod === "device_location"
                      ? "device"
                      : legacyMethod === "official_import"
                        ? "official"
                        : "local"
                  ) as LocationProvenance["provider"],
                  accuracyM: draft.gpsAccuracyM
                    ? Number(draft.gpsAccuracyM)
                    : undefined,
                  userConfirmed: true,
                },
                confirmedAt: draft.updatedAt ?? new Date().toISOString(),
                superseded: false,
              },
            ]
          : [];
    const migratedCondition = migrateLegacyCondition(
      draft.conditionScore,
      sourceSchemaVersion >= 2 ? "5_IS_BEST" : undefined,
    );
    const definition = infrastructureType(draft.assetType);
    const next: A2AssetDraft = {
      ...draft,
      schemaVersion: STAGE2_SCHEMA_VERSION,
      settlementArea: draft.settlementArea ?? draft.serviceAreas ?? "",
      repositoryVersion:
        draft.repositoryVersion ?? infrastructureRepository.repository_version,
      locationMethod: legacyMethod,
      locationConfidence: draft.locationConfidence ?? "",
      pendingLocationProvenance:
        draft.pendingLocationProvenance ?? { provider: "local" },
      geometryVersions,
      currentGeometrySeq:
        draft.currentGeometrySeq ??
        geometryVersions.find((item) => !item.superseded)?.seq ??
        0,
      quickFacts: draft.quickFacts ?? {},
      typeHistory: draft.typeHistory ?? [],
      enrichment: buildEnrichmentCards(
        definition?.enrichment_cards ?? [],
        draft.enrichment ?? {},
      ),
      evidence: (draft.evidence ?? []).map((item) =>
        normalizeEvidence(item, draft.sensitivity),
      ),
      conditionScore: migratedCondition.code,
      conditionBasis: migratedCondition.reviewRequired
        ? [draft.conditionBasis, "Legacy numeric condition direction requires review."]
            .filter(Boolean)
            .join(" ")
        : draft.conditionBasis,
      otherDetails: draft.otherDetails ?? fresh.a2Draft.otherDetails,
      duplicateAcknowledged: draft.duplicateAcknowledged ?? false,
      importBatchId: draft.importBatchId ?? "",
      importRowNumber: draft.importRowNumber ?? null,
      syncState: draft.syncState ?? "local_draft",
      resume: draft.resume ?? fresh.a2Draft.resume,
      createdAt: draft.createdAt ?? new Date().toISOString(),
      updatedAt: draft.updatedAt ?? new Date().toISOString(),
      commonCore: draft.commonCore ?? commonCoreSnapshot(draft),
    };
    return withDraftMetadata(next);
  };
  return {
    ...fresh,
    ...sourceState,
    schemaVersion: STAGE2_SCHEMA_VERSION,
    a2Draft: normalizeDraft(sourceState.a2Draft ?? fresh.a2Draft),
    assets: (sourceState.assets ?? []).map((record) => {
      const draft = normalizeDraft(record);
      const quality = missionQuality(draft);
      return {
        ...record,
        ...draft,
        qualityBadge: record.qualityBadge ?? quality.badge,
        quickFactsCaptured: record.quickFactsCaptured ?? quality.captured,
        captureDisposition: record.captureDisposition ?? "complete",
      };
    }),
    importBatches: (sourceState.importBatches ?? []).map((batch) => ({
      ...batch,
      rows: (batch.rows ?? []).map((row) => ({
        ...row,
        locationStatus:
          row.locationStatus ??
          (row.reasons?.includes("LOCATION_UNCERTAIN until geometry is verified")
            ? "needs_location"
            : "located"),
      })),
    })),
    favouriteTypeCodes: sourceState.favouriteTypeCodes ?? [],
    recentTypeCodes: sourceState.recentTypeCodes ?? [],
  };
}

export function validateOfficial(draft: A3OfficialDraft) {
  const errors: Record<string, string> = {};
  if (!draft.officialRole.trim()) errors.officialRole = "Record the official role.";
  if (!draft.organisation.trim()) errors.organisation = "Record the organisation or department.";
  if (!draft.serviceArea.trim()) errors.serviceArea = "Choose or describe the service area.";
  if (!draft.mainSupplyArrangements.length) {
    errors.mainSupplyArrangements = "Choose at least one main supply arrangement.";
  }
  const days = Number(draft.typicalSupplyDays);
  if (draft.typicalSupplyDays === "" || days < 0 || days > 7) errors.typicalSupplyDays = "Enter 0 to 7 days.";
  if (!draft.interruptions) errors.interruptions = "Choose an interruption frequency.";
  if (!draft.advanceNoticePractice) errors.advanceNoticePractice = "Choose the normal notice practice.";
  if (!draft.tankerDependence) errors.tankerDependence = "Choose the level of tanker dependence.";
  if (!draft.reliabilityRating) errors.reliabilityRating = "Rate official reliability from 1 to 5.";
  if (!draft.reliabilityReason.trim()) {
    errors.reliabilityReason = "Briefly explain the reliability rating.";
  }
  if (!draft.biggestConstraint) errors.biggestConstraint = "Choose the biggest current constraint.";
  if (!draft.sourceReferences.trim()) errors.sourceReferences = "Provide at least one source reference.";
  if (!draft.permissionToFollowUp) errors.permissionToFollowUp = "Record whether follow-up is permitted.";
  if (draft.coverageEstimatePct && (Number(draft.coverageEstimatePct) < 0 || Number(draft.coverageEstimatePct) > 100)) {
    errors.coverageEstimatePct = "Coverage must be from 0 to 100%.";
  }
  if (draft.coverageEstimatePct && !draft.coverageYear) {
    errors.coverageYear = "Record the year for the coverage estimate.";
  }
  if (draft.coverageEstimatePct && !draft.coverageBasis.trim()) {
    errors.coverageBasis = "Record the basis for the coverage estimate.";
  }
  if (draft.tankerDependence && draft.tankerDependence !== "None" && !draft.tankerAreas.trim()) {
    errors.tankerAreas = "Identify the areas normally served by tankers.";
  }
  return errors;
}

export function piiFlags(draft: A3CommunityDraft) {
  const text = `${draft.daysNote} ${draft.situationChangeReason} ${draft.priorityChange}`;
  const patterns = [
    /\b[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}\b/i,
    /\b(?:\+27|0)[6-8][0-9][ -]?[0-9]{3}[ -]?[0-9]{4}\b/,
    /\b[0-9]{13}\b/,
    /\b(?:street|road|avenue|close|crescent|house|stand|erf)\b/i,
  ];
  return patterns.some((pattern) => pattern.test(text)) ? ["PII_SUSPECTED"] : [];
}

export function validateCommunity(draft: A3CommunityDraft) {
  const errors: Record<string, string> = {};
  if (!draft.areaCode) errors.areaCode = "Choose the reporting area.";
  if (!draft.mainWaterSources.length) errors.mainWaterSources = "Choose at least one water source.";
  if (!draft.mainWaterSource) errors.mainWaterSource = "Mark the main water source.";
  if (!draft.daysVaries) {
    const days = Number(draft.daysAvailable);
    if (draft.daysAvailable === "" || !Number.isInteger(days) || days < 0 || days > 7) {
      errors.daysAvailable = "Enter a whole number from 0 to 7, or choose Varies / unsure.";
    }
  }
  if (!draft.interruptionFrequency) errors.interruptionFrequency = "Choose an interruption frequency.";
  if (!draft.safetyConfidence) errors.safetyConfidence = "Rate confidence in water safety.";
  if (!draft.overallSatisfaction) errors.overallSatisfaction = "Rate overall satisfaction.";
  if (!draft.representativeProvided) errors.representativeProvided = "Record whether a representative facilitated the response.";
  if (draft.representativeProvided === "yes" && !draft.representativeType) {
    errors.representativeType = "Choose the representative type.";
  }
  if (draft.representativeProvided === "yes" && !draft.batchCode.trim()) {
    errors.batchCode = "Enter the linked engagement batch code.";
  }
  return errors;
}
